import { useEffect, useMemo, useState } from 'react'
import { PageContainer, PageHeader } from '../../components/Layout'
import { Badge, Button, Card, CardContent, Input, Modal, Select } from '../../components/UI'
import { rentalTechService } from '../../services/api'
import { equipmentOptions, useRentalTechData } from './useRentalTechData'
import { Search, Eye, Edit, Trash2, Plus, Wrench, Calendar, DollarSign, Activity, FileText, Settings, Truck, X } from 'lucide-react'

const defaultEquipment = {
  code: '',
  name: '',
  type: '',
  brand: '',
  model: '',
  serialNumber: '',
  year: new Date().getFullYear(),
  assetValue: 0,
  hourlyRate: 0,
  dailyRate: 0,
  weeklyRate: 0,
  monthlyRate: 0,
  lat: 0,
  lng: 0
}

const toLocalDateInput = (value) => {
  if (!value) return ''
  const date = new Date(value)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

const formatCurrency = (value) => {
  if (!value && value !== 0) return '-'
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(value)
}

export default function RentalTechEquipments() {
  const { equipments, refresh } = useRentalTechData()
  const [equipmentForm, setEquipmentForm] = useState(defaultEquipment)
  const [selectedEquipmentId, setSelectedEquipmentId] = useState('')
  const [lifeSheetForm, setLifeSheetForm] = useState({
    acquisitionDateUtc: toLocalDateInput(new Date()),
    acquisitionCost: 0,
    currentEngineHours: 0,
    lastPreventiveMaintenanceUtc: '',
    lastCorrectiveMaintenanceUtc: '',
    insurancePolicy: '',
    technicalSpecsJson: '[]',
    documentsJson: '[]',
    notes: '',
    events: []
  })
  const [message, setMessage] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showLifeSheetModal, setShowLifeSheetModal] = useState(false)
  const [editingEquipment, setEditingEquipment] = useState(null)
  const [selectedLifeSheetEquipment, setSelectedLifeSheetEquipment] = useState(null)
  const [equipmentToDelete, setEquipmentToDelete] = useState(null)

  const options = useMemo(() => equipmentOptions(equipments), [equipments])

  // Filtered equipment for table view
  const filteredEquipments = useMemo(() => {
    if (!searchTerm) return equipments
    const term = searchTerm.toLowerCase()
    return equipments.filter(eq => 
      (eq.name && eq.name.toLowerCase().includes(term)) ||
      (eq.code && eq.code.toLowerCase().includes(term)) ||
      (eq.type && eq.type.toLowerCase().includes(term)) ||
      (eq.brand && eq.brand.toLowerCase().includes(term)) ||
      (eq.model && eq.model.toLowerCase().includes(term))
    )
  }, [equipments, searchTerm])

  useEffect(() => {
    if (!selectedEquipmentId && options.length > 0) {
      setSelectedEquipmentId(options[0].value)
    }
  }, [options, selectedEquipmentId])

  const saveEquipment = async () => {
    setMessage('')
    const payload = {
      ...equipmentForm,
      year: Number(equipmentForm.year || 0),
      assetValue: Number(equipmentForm.assetValue || 0),
      hourlyRate: Number(equipmentForm.hourlyRate || 0),
      dailyRate: Number(equipmentForm.dailyRate || 0),
      weeklyRate: Number(equipmentForm.weeklyRate || 0),
      monthlyRate: Number(equipmentForm.monthlyRate || 0),
      lat: Number(equipmentForm.lat || 0),
      lng: Number(equipmentForm.lng || 0)
    }

    await rentalTechService.saveEquipment(payload)
    setEquipmentForm(defaultEquipment)
    await refresh()
    setShowCreateModal(false)
    setMessage('Equipo creado correctamente.')
  }

  const updateEquipment = async () => {
    if (!editingEquipment) return
    setMessage('')
    const payload = {
      ...equipmentForm,
      id: editingEquipment.id,
      year: Number(equipmentForm.year || 0),
      assetValue: Number(equipmentForm.assetValue || 0),
      hourlyRate: Number(equipmentForm.hourlyRate || 0),
      dailyRate: Number(equipmentForm.dailyRate || 0),
      weeklyRate: Number(equipmentForm.weeklyRate || 0),
      monthlyRate: Number(equipmentForm.monthlyRate || 0),
      lat: Number(equipmentForm.lat || 0),
      lng: Number(equipmentForm.lng || 0)
    }

    await rentalTechService.saveEquipment(payload)
    setEquipmentForm(defaultEquipment)
    setEditingEquipment(null)
    await refresh()
    setShowEditModal(false)
    setMessage('Equipo actualizado correctamente.')
  }

  const deleteEquipment = async () => {
    if (!equipmentToDelete) return
    try {
      await rentalTechService.deleteEquipment(equipmentToDelete.id)
      setMessage('Equipo eliminado correctamente.')
      setEquipmentToDelete(null)
      await refresh()
    } catch (error) {
      setMessage('Error al eliminar el equipo.')
    }
  }

  const saveLifeSheet = async () => {
    if (!selectedEquipmentId) {
      setMessage('Primero crea o selecciona un equipo.')
      return
    }

    const payload = {
      acquisitionDateUtc: new Date(lifeSheetForm.acquisitionDateUtc || new Date()).toISOString(),
      acquisitionCost: Number(lifeSheetForm.acquisitionCost || 0),
      currentEngineHours: Number(lifeSheetForm.currentEngineHours || 0),
      lastPreventiveMaintenanceUtc: lifeSheetForm.lastPreventiveMaintenanceUtc ? new Date(lifeSheetForm.lastPreventiveMaintenanceUtc).toISOString() : null,
      lastCorrectiveMaintenanceUtc: lifeSheetForm.lastCorrectiveMaintenanceUtc ? new Date(lifeSheetForm.lastCorrectiveMaintenanceUtc).toISOString() : null,
      insurancePolicy: lifeSheetForm.insurancePolicy,
      technicalSpecsJson: lifeSheetForm.technicalSpecsJson,
      documentsJson: lifeSheetForm.documentsJson,
      notes: lifeSheetForm.notes,
      events: lifeSheetForm.events
    }

    await rentalTechService.saveEquipmentLifeSheet(selectedEquipmentId, payload)
    setMessage('Hoja de vida guardada correctamente.')
  }

  const addEvent = () => {
    setLifeSheetForm((prev) => ({
      ...prev,
      events: [...prev.events, {
        eventDateUtc: new Date().toISOString(),
        eventType: 'preventivo',
        description: '',
        cost: 0
      }]
    }))
  }

  const updateEvent = (idx, field, value) => {
    setLifeSheetForm((prev) => {
      const events = [...prev.events]
      events[idx] = { ...events[idx], [field]: value }
      return { ...prev, events }
    })
  }

  const openEditModal = (equipment) => {
    setEditingEquipment(equipment)
    setEquipmentForm({
      code: equipment.code || '',
      name: equipment.name || '',
      type: equipment.type || '',
      brand: equipment.brand || '',
      model: equipment.model || '',
      serialNumber: equipment.serialNumber || '',
      year: equipment.year || new Date().getFullYear(),
      assetValue: equipment.assetValue || 0,
      hourlyRate: equipment.hourlyRate || 0,
      dailyRate: equipment.dailyRate || 0,
      weeklyRate: equipment.weeklyRate || 0,
      monthlyRate: equipment.monthlyRate || 0,
      lat: equipment.lat || 0,
      lng: equipment.lng || 0
    })
    setShowEditModal(true)
  }

  const openLifeSheetModal = async (equipment) => {
    setSelectedLifeSheetEquipment(equipment)
    setSelectedEquipmentId(equipment.id)
    try {
      const response = await rentalTechService.getEquipmentLifeSheet(equipment.id)
      const row = response?.data?.data
      if (row) {
        setLifeSheetForm({
          acquisitionDateUtc: toLocalDateInput(row.acquisition_date_utc),
          acquisitionCost: row.acquisition_cost || 0,
          currentEngineHours: row.current_engine_hours || 0,
          lastPreventiveMaintenanceUtc: toLocalDateInput(row.last_preventive_maintenance_utc),
          lastCorrectiveMaintenanceUtc: toLocalDateInput(row.last_corrective_maintenance_utc),
          insurancePolicy: row.insurance_policy || '',
          technicalSpecsJson: row.technical_specs_json || '[]',
          documentsJson: row.documents_json || '[]',
          notes: row.notes || '',
          events: row.events || []
        })
      } else {
        setLifeSheetForm({
          acquisitionDateUtc: toLocalDateInput(new Date()),
          acquisitionCost: 0,
          currentEngineHours: 0,
          lastPreventiveMaintenanceUtc: '',
          lastCorrectiveMaintenanceUtc: '',
          insurancePolicy: '',
          technicalSpecsJson: '[]',
          documentsJson: '[]',
          notes: '',
          events: []
        })
      }
    } catch {
      setLifeSheetForm({
        acquisitionDateUtc: toLocalDateInput(new Date()),
        acquisitionCost: 0,
        currentEngineHours: 0,
        lastPreventiveMaintenanceUtc: '',
        lastCorrectiveMaintenanceUtc: '',
        insurancePolicy: '',
        technicalSpecsJson: '[]',
        documentsJson: '[]',
        notes: '',
        events: []
      })
    }
    setShowLifeSheetModal(true)
  }

  const closeLifeSheetModal = () => {
    setShowLifeSheetModal(false)
    setSelectedLifeSheetEquipment(null)
  }

  const getEventTypeBadge = (type) => {
    const normalizedType = type?.toLowerCase()
    if (normalizedType === 'preventivo') return <Badge variant="info">Preventivo</Badge>
    if (normalizedType === 'correctivo') return <Badge variant="warning">Correctivo</Badge>
    if (normalizedType === 'inspeccion') return <Badge variant="success">Inspección</Badge>
    return <Badge variant="gray">{type}</Badge>
  }

  const handleCreateClick = () => {
    setEquipmentForm(defaultEquipment)
    setShowCreateModal(true)
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <PageHeader
          title="Equipos y Hoja de Vida"
          subtitle="Crea maquinaria y diligencia su historial técnico, operativo y documental"
        />

        {message ? <div className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded px-3 py-2">{message}</div> : null}

        {/* Table View - Main View */}
        <Card>
          <CardContent className="p-5">
            {/* Header with Create Button */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900 text-lg">Lista de Equipos</h3>
              <Button onClick={handleCreateClick}>
                <Plus className="w-4 h-4 mr-1" /> Crear Equipo
              </Button>
            </div>

            {/* Search Filter */}
            <div className="mb-4">
              <Input
                placeholder="Buscar por código, nombre, tipo, marca o modelo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search className="w-4 h-4" />}
              />
            </div>

            {/* Equipment Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marca</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modelo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Año</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEquipments.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        {searchTerm ? 'No se encontraron equipos con ese criterio de búsqueda' : 'No hay equipos registrados'}
                      </td>
                    </tr>
                  ) : (
                    filteredEquipments.map((eq) => (
                      <tr key={eq.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{eq.code || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{eq.name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{eq.type || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{eq.brand || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{eq.model || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{eq.year || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatCurrency(eq.assetValue)}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="secondary"
                              onClick={() => openEditModal(eq)}
                              title="Editar"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="danger"
                              onClick={() => setEquipmentToDelete(eq)}
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="primary"
                              onClick={() => openLifeSheetModal(eq)}
                              title="Ver Hoja de Vida"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Create Equipment Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          size="xl"
          title={
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary-600" />
              <span>Crear Nuevo Equipo</span>
            </div>
          }
        >
          <div className="space-y-4">
            {/* Basic Info Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">Información Básica</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Código *" placeholder="Ej: EQ-001" value={equipmentForm.code} onChange={(e) => setEquipmentForm({ ...equipmentForm, code: e.target.value })} />
                <Input label="Nombre *" placeholder="Ej: Excavadora CAT 320" value={equipmentForm.name} onChange={(e) => setEquipmentForm({ ...equipmentForm, name: e.target.value })} />
                <Input label="Tipo" placeholder="Ej: Excavadora" value={equipmentForm.type} onChange={(e) => setEquipmentForm({ ...equipmentForm, type: e.target.value })} />
                <Input label="Marca" placeholder="Ej: Caterpillar" value={equipmentForm.brand} onChange={(e) => setEquipmentForm({ ...equipmentForm, brand: e.target.value })} />
                <Input label="Modelo" placeholder="Ej: 320" value={equipmentForm.model} onChange={(e) => setEquipmentForm({ ...equipmentForm, model: e.target.value })} />
                <Input label="Número de Serie" placeholder="Ej: CAT0320XXX" value={equipmentForm.serialNumber} onChange={(e) => setEquipmentForm({ ...equipmentForm, serialNumber: e.target.value })} />
              </div>
            </div>

            {/* Year and Value Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">Año y Valor</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input type="number" label="Año" placeholder="Ej: 2024" value={equipmentForm.year} onChange={(e) => setEquipmentForm({ ...equipmentForm, year: e.target.value })} />
                <Input type="number" label="Valor Activo ($)" placeholder="Ej: 500000000" value={equipmentForm.assetValue} onChange={(e) => setEquipmentForm({ ...equipmentForm, assetValue: e.target.value })} />
              </div>
            </div>

            {/* Rates Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">Tarifas de Alquiler</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input type="number" label="Tarifa por Hora ($)" placeholder="Ej: 150000" value={equipmentForm.hourlyRate} onChange={(e) => setEquipmentForm({ ...equipmentForm, hourlyRate: e.target.value })} />
                <Input type="number" label="Tarifa por Día ($)" placeholder="Ej: 800000" value={equipmentForm.dailyRate} onChange={(e) => setEquipmentForm({ ...equipmentForm, dailyRate: e.target.value })} />
                <Input type="number" label="Tarifa por Semana ($)" placeholder="Ej: 4500000" value={equipmentForm.weeklyRate} onChange={(e) => setEquipmentForm({ ...equipmentForm, weeklyRate: e.target.value })} />
                <Input type="number" label="Tarifa por Mes ($)" placeholder="Ej: 15000000" value={equipmentForm.monthlyRate} onChange={(e) => setEquipmentForm({ ...equipmentForm, monthlyRate: e.target.value })} />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
              <Button onClick={saveEquipment}>Crear Equipo</Button>
            </div>
          </div>
        </Modal>

        {/* Edit Equipment Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={() => { setShowEditModal(false); setEditingEquipment(null); }}
          size="xl"
          title={
            <div className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary-600" />
              <span>Editar Equipo - {editingEquipment?.name}</span>
            </div>
          }
        >
          <div className="space-y-4">
            {/* Basic Info Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">Información Básica</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Código *" placeholder="Ej: EQ-001" value={equipmentForm.code} onChange={(e) => setEquipmentForm({ ...equipmentForm, code: e.target.value })} />
                <Input label="Nombre *" placeholder="Ej: Excavadora CAT 320" value={equipmentForm.name} onChange={(e) => setEquipmentForm({ ...equipmentForm, name: e.target.value })} />
                <Input label="Tipo" placeholder="Ej: Excavadora" value={equipmentForm.type} onChange={(e) => setEquipmentForm({ ...equipmentForm, type: e.target.value })} />
                <Input label="Marca" placeholder="Ej: Caterpillar" value={equipmentForm.brand} onChange={(e) => setEquipmentForm({ ...equipmentForm, brand: e.target.value })} />
                <Input label="Modelo" placeholder="Ej: 320" value={equipmentForm.model} onChange={(e) => setEquipmentForm({ ...equipmentForm, model: e.target.value })} />
                <Input label="Número de Serie" placeholder="Ej: CAT0320XXX" value={equipmentForm.serialNumber} onChange={(e) => setEquipmentForm({ ...equipmentForm, serialNumber: e.target.value })} />
              </div>
            </div>

            {/* Year and Value Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">Año y Valor</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input type="number" label="Año" placeholder="Ej: 2024" value={equipmentForm.year} onChange={(e) => setEquipmentForm({ ...equipmentForm, year: e.target.value })} />
                <Input type="number" label="Valor Activo ($)" placeholder="Ej: 500000000" value={equipmentForm.assetValue} onChange={(e) => setEquipmentForm({ ...equipmentForm, assetValue: e.target.value })} />
              </div>
            </div>

            {/* Rates Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">Tarifas de Alquiler</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input type="number" label="Tarifa por Hora ($)" placeholder="Ej: 150000" value={equipmentForm.hourlyRate} onChange={(e) => setEquipmentForm({ ...equipmentForm, hourlyRate: e.target.value })} />
                <Input type="number" label="Tarifa por Día ($)" placeholder="Ej: 800000" value={equipmentForm.dailyRate} onChange={(e) => setEquipmentForm({ ...equipmentForm, dailyRate: e.target.value })} />
                <Input type="number" label="Tarifa por Semana ($)" placeholder="Ej: 4500000" value={equipmentForm.weeklyRate} onChange={(e) => setEquipmentForm({ ...equipmentForm, weeklyRate: e.target.value })} />
                <Input type="number" label="Tarifa por Mes ($)" placeholder="Ej: 15000000" value={equipmentForm.monthlyRate} onChange={(e) => setEquipmentForm({ ...equipmentForm, monthlyRate: e.target.value })} />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="secondary" onClick={() => { setShowEditModal(false); setEditingEquipment(null); }}>Cancelar</Button>
              <Button onClick={updateEquipment}>Guardar Cambios</Button>
            </div>
          </div>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={!!equipmentToDelete}
          onClose={() => setEquipmentToDelete(null)}
          size="sm"
          title={
            <div className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-error" />
              <span>Confirmar Eliminación</span>
            </div>
          }
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              ¿Está seguro de que desea eliminar el equipo <strong>{equipmentToDelete?.name}</strong> ({equipmentToDelete?.code})?
            </p>
            <p className="text-sm text-error">Esta acción no se puede deshacer.</p>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="secondary" onClick={() => setEquipmentToDelete(null)}>Cancelar</Button>
              <Button variant="danger" onClick={deleteEquipment}>Eliminar</Button>
            </div>
          </div>
        </Modal>

        {/* Industrial Life Sheet Modal */}
        <Modal
          isOpen={showLifeSheetModal}
          onClose={closeLifeSheetModal}
          size="xl"
          title={
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary-600" />
              <span>Hoja de Vida - {selectedLifeSheetEquipment?.name || 'Equipo'}</span>
            </div>
          }
        >
          <div className="space-y-6">
            {/* Equipment Header - Industrial Style */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-lg p-4 text-white">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs text-slate-300 uppercase tracking-wider">Código</div>
                  <div className="font-mono text-lg">{selectedLifeSheetEquipment?.code || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-300 uppercase tracking-wider">Marca</div>
                  <div className="font-semibold">{selectedLifeSheetEquipment?.brand || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-300 uppercase tracking-wider">Modelo</div>
                  <div className="font-semibold">{selectedLifeSheetEquipment?.model || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-300 uppercase tracking-wider">Año</div>
                  <div className="font-semibold">{selectedLifeSheetEquipment?.year || '-'}</div>
                </div>
              </div>
            </div>

            {/* Key Metrics - Industrial Dashboard Style */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <Activity className="w-4 h-4" />
                  <span className="text-xs uppercase font-medium">Horas de Motor</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">{lifeSheetForm.currentEngineHours || 0}</div>
              </div>
              <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-xs uppercase font-medium">Costo Adquisición</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">{formatCurrency(lifeSheetForm.acquisitionCost)}</div>
              </div>
              <div className="bg-amber-50 border-l-4 border-amber-600 p-4 rounded">
                <div className="flex items-center gap-2 text-amber-600 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs uppercase font-medium">Últ. Mant. Prev.</span>
                </div>
                <div className="text-lg font-bold text-gray-900">{lifeSheetForm.lastPreventiveMaintenanceUtc || '-'}</div>
              </div>
              <div className="bg-purple-50 border-l-4 border-purple-600 p-4 rounded">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <Wrench className="w-4 h-4" />
                  <span className="text-xs uppercase font-medium">Últ. Mant. Corr.</span>
                </div>
                <div className="text-lg font-bold text-gray-900">{lifeSheetForm.lastCorrectiveMaintenanceUtc || '-'}</div>
              </div>
            </div>

            {/* Insurance and Technical Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Información de Seguro
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Póliza:</span>
                    <span className="text-sm font-medium">{lifeSheetForm.insurancePolicy || 'No asignada'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Fecha Adquisición:</span>
                    <span className="text-sm font-medium">{lifeSheetForm.acquisitionDateUtc || '-'}</span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Especificaciones Técnicas
                </h4>
                <div className="text-sm text-gray-600 font-mono bg-white p-2 rounded border max-h-24 overflow-auto">
                  {lifeSheetForm.technicalSpecsJson || 'Sin especificaciones'}
                </div>
              </div>
            </div>

            {/* Events History Table - Industrial Style */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-slate-700 px-4 py-3 flex items-center justify-between">
                <h4 className="font-semibold text-white flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Historial de Eventos
                </h4>
                <Badge variant="gray">{lifeSheetForm.events?.length || 0} eventos</Badge>
              </div>
              <div className="overflow-x-auto max-h-64">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Costo</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {!lifeSheetForm.events || lifeSheetForm.events.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                          No hay eventos registrados
                        </td>
                      </tr>
                    ) : (
                      lifeSheetForm.events.map((event, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-900 font-mono">{toLocalDateInput(event.eventDateUtc)}</td>
                          <td className="px-4 py-2">{getEventTypeBadge(event.eventType)}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{event.description || '-'}</td>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{formatCurrency(event.cost)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Add Event Section */}
            <div className="border rounded p-3 space-y-2 bg-slate-50">
              <div className="flex justify-between items-center">
                <p className="text-sm font-medium text-gray-900 flex items-center gap-1"><Activity className="w-4 h-4" /> Agregar Evento</p>
                <Button size="sm" variant="secondary" onClick={addEvent}>Nuevo Evento</Button>
              </div>
              {lifeSheetForm.events.map((event, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-2 bg-white p-2 rounded border">
                  <Input type="date" label="Fecha" value={toLocalDateInput(event.eventDateUtc)} onChange={(e) => updateEvent(idx, 'eventDateUtc', new Date(e.target.value).toISOString())} />
                  <Input label="Tipo" placeholder="preventivo/correctivo" value={event.eventType} onChange={(e) => updateEvent(idx, 'eventType', e.target.value)} />
                  <Input label="Descripción" placeholder="Descripción del evento" value={event.description} onChange={(e) => updateEvent(idx, 'description', e.target.value)} />
                  <Input type="number" label="Costo ($)" placeholder="0" value={event.cost} onChange={(e) => updateEvent(idx, 'cost', Number(e.target.value || 0))} />
                </div>
              ))}
            </div>

            {/* Notes */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Notas
              </h4>
              <Input 
                label="" 
                placeholder="Agregar notas sobre el equipo..." 
                value={lifeSheetForm.notes} 
                onChange={(e) => setLifeSheetForm({ ...lifeSheetForm, notes: e.target.value })} 
              />
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="secondary" onClick={closeLifeSheetModal}>Cerrar</Button>
              <Button onClick={async () => { await saveLifeSheet(); closeLifeSheetModal(); }}>Guardar Cambios</Button>
            </div>
          </div>
        </Modal>
      </div>
    </PageContainer>
  )
}
